import { Brand, createBranded } from '@deltasierra/type-utilities';
import { HasId, FullAuditable, Omit, ApiRoute, HttpMethod } from '../common';
import { StripeChargeId } from '../integration';
import { User } from '../user/User';
import { ISendToSKUPrintProviderData } from '../publish/print';
import { Agency } from '../agency';
import { PrintProviderSKUGroup } from './PrintProviderSKUGroup';
import { PrintProviderSKU } from './PrintProviderSKU';
import { PrintProviderStats } from './PrintProviderStats';
import { CartItemWithFullDetailsDto, CartItemWithBasicDetailsDto } from './PrintProviderShoppingCartItem';
import { PrintProviderId, PrintProvider } from './PrintProvider';

export type PrintProviderShoppingCartOrderId = Brand<number, 'PrintProviderShoppingCartOrderId'>;
export const PrintProviderShoppingCartOrderId = createBranded<PrintProviderShoppingCartOrderId>();

export interface PrintProviderShoppingCartOrder extends HasId<PrintProviderShoppingCartOrderId>, FullAuditable {
    status: OrderStatus;
    printProviderId: PrintProviderId;
    stripeChargeId: StripeChargeId | null;
}

// We want to keep updatedAt for the datetime of order being finalised
export type PrintProviderShoppingCartOrderDto = Omit<
    PrintProviderShoppingCartOrder,
    'createdAt' | 'createdById' | 'deletedAt' | 'updatedById'
>;

export interface ShoppingCartOrderWithPrintProviderDto extends PrintProviderShoppingCartOrderDto {
    printProvider: CartPrintProviderDetails;
}

type CartPrintProviderDetails = Pick<
    PrintProvider,
    'businessName' | 'email' | 'printProviderSKUGroupId' | 'stripeConfigurationId'
> & {
    client: CartClientAgencyDetails;
    printProviderSKUGroup: CartPrintProviderSKUGroupDetails;
};

export interface CartClientAgencyDetails {
    title: string;
    agency: CartAgencyDetails;
}

type CartAgencyDetails = Pick<Agency, 'company' | 'id' | 'localeCode'>;

export interface ShoppingCartOrderWithLocationsDto extends PrintProviderShoppingCartOrderDto {
    printProviderShoppingCartItems: CartItemWithBasicDetailsDto[];
}

export interface ShoppingCartOrderWithLocationsAndPrintProviderDto extends ShoppingCartOrderWithLocationsDto {
    printProvider: CartPrintProviderWithBasicDetails;
}

export type CartPrintProviderSKUGroupDetails = Pick<
    PrintProviderSKUGroup,
    'defaultPriceCurrency' | 'paymentGateway' | 'shippingFeePriceAmountInCents'
>;

type CartPrintProviderWithBasicDetails = Pick<PrintProvider, 'businessName'>;

export interface ShoppingCartOrderWithAllDetailsDto extends ShoppingCartOrderWithPrintProviderDto {
    printProviderShoppingCartItems: CartItemWithFullDetailsDto[];
    printProviderSKUGroup: CartPrintProviderSKUGroupDetails;
    uniqueLocationsCount: number;
}

export interface CreateOrUpdateShoppingCartData {
    user: User;
    data: {
        quantity: number;
        skuData: ISendToSKUPrintProviderData;
    };
    provider: PrintProvider;
    printProviderSKU: PrintProviderSKU;
    printProviderStats: PrintProviderStats;
}

export type ShoppingCartOrderWithItemCount = Pick<PrintProviderShoppingCartOrder, 'id'> & {
    itemCount: number;
};

export type OrderStatus = 'finalised' | 'pending';

export interface OrdersApiParams {
    status: OrderStatus;
}

export interface OrderApiParams {
    orderId: PrintProviderShoppingCartOrderId;
}

export const PrintProviderShoppingCartOrdersApi = {
    getOrder: new ApiRoute<ShoppingCartOrderWithAllDetailsDto, OrderApiParams, void, void>(HttpMethod.Get, path =>
        path.literal('printProviderShoppingCartOrders').param('orderId'),
    ),
    getOrders: new ApiRoute<PrintProviderShoppingCartOrderId[], OrdersApiParams, void, void>(HttpMethod.Get, path =>
        path.literal('printProviderShoppingCartOrders').literal('all').param('status'),
    ),
    getOrdersWithItemCount: new ApiRoute<ShoppingCartOrderWithItemCount[], OrdersApiParams, void, void>(
        HttpMethod.Get,
        path => path.literal('shoppingCartOrderItemCount').param('status'),
    ),
};
